/** Server-path error type (PLAN.md C3), split out so `edge-transport.ts` and `index.ts` do not import each other at load. */
/**
 * Fix-all C6: `server_error` = our side answered 5xx (not the user's connection); `rate_limited` = a 429,
 * with the server's wait in `retryAfterSec` when it gave one. `refused` stays for 402 / tagged enforcement.
 */
/**
 * Usage-QA C1 (PLAN.md, 2026-10-07): `network` = the connection really dropped (the ONLY code that says "Check your
 * connection"), `unreadable` = the browser could not read the picked file, or the edge could not decode it, `too-large`
 * = over the upload limit. The phase codes stay for what is neither (index.ts classifies by phase).
 */
export type ServerErrorCode = 'upload' | 'schedule' | 'refused' | 'failed' | 'timeout' | 'cancelled' | 'download' | 'server_error' | 'rate_limited'
    | 'network' | 'unreadable' | 'too-large';

export const DEFAULT_MESSAGES: Record<ServerErrorCode, string> = {
    // J2-1 / J16-7: a phase failure that is not a lost connection must not blame the user's network.
    upload: 'We couldn\'t upload this file.',
    schedule: 'We couldn\'t start processing this file. Please try again.',
    refused: 'You\'ve reached the limit for now. Please try again later.',
    failed: 'We couldn\'t process this file.',
    timeout: 'Processing this file took too long. Please try again.',
    cancelled: 'Cancelled.',
    download: 'We processed this file but couldn\'t fetch the result. Please try again.',
    server_error: 'Our server had a problem. Please try again.',
    rate_limited: 'You\'ve reached the limit for now. Please try again later.',
    network: 'We couldn\'t reach our server. Check your connection and try again.',
    unreadable: 'This file can\'t be read. Pick it again, or choose a different file.',
    'too-large': 'This file is too large to process.',
};

/** C1: only a lost connection or a timeout can succeed on a second try; everything else fails the same way again. */
export const RETRYABLE_CODES: ReadonlyArray<ServerErrorCode> = ['network', 'timeout'];

/** C1: how many times "Try again" may re-run one file before the button goes away (J2-1: it used to loop forever). */
export const MAX_RETRIES = 2;

export function failureRetryable(code: string | null | undefined): boolean {
    return !!code && (RETRYABLE_CODES as ReadonlyArray<string>).indexOf(code) >= 0;
}

/** True while a file that failed with [code] after [retriesSoFar] "Try again"s may be offered another one. */
export function canRetry(code: string | null | undefined, retriesSoFar: number): boolean {
    return failureRetryable(code) && (Number(retriesSoFar) || 0) < MAX_RETRIES;
}

/** The sentence for a phase failure while the browser reports no network (IMAGEEDITOR-14). */
export const OFFLINE_MESSAGE = 'You\'re offline. We\'ll retry when you\'re back, or try again.';

export class ServerEditError extends Error {
    readonly code: ServerErrorCode;
    readonly userMessage: string;
    /** C6: seconds the server asked us to wait (429 / 503 Retry-After), when it said. */
    readonly retryAfterSec?: number;
    constructor(code: ServerErrorCode, userMessage?: string, cause?: unknown, retryAfterSec?: number) {
        super(`server edit ${code}${cause instanceof Error && cause.message ? `: ${cause.message}` : ''}`);
        this.name = 'ServerEditError';
        this.code = code;
        this.userMessage = (userMessage && userMessage.trim()) || DEFAULT_MESSAGES[code];
        if (typeof retryAfterSec === 'number' && Number.isFinite(retryAfterSec) && retryAfterSec > 0) this.retryAfterSec = Math.ceil(retryAfterSec);
    }
}
